export type SandboxState = 'dirty' | 'published';

export interface SandboxPublishStatus {
  has_pending: boolean;
  sandbox_dirty: boolean;
  ready_items_count: number;
  ready_snapshots_count: number;
  ready_gifts_count: number;
  ready_season_passes_count: number;
}

export interface Sandbox {
  id: string;
  created_at: string;
  updated_at: string;
  code: string;
  name: string;
  description: string;
  is_active: boolean;
  game_id: string;
  game_code: string;
  state: SandboxState;
  publish_status?: SandboxPublishStatus;
}

/** What a publish would ship, as "2 snapshot(s)" parts; empty kinds are left out. */
export const pendingCountParts = (status: SandboxPublishStatus): string[] => {
  // ready_items_count includes the season passes, which are listed on their own.
  const catalogItems =
    status.ready_items_count - status.ready_season_passes_count;
  const counts: [number, string][] = [
    [catalogItems, 'catalog item(s)'],
    [status.ready_snapshots_count, 'snapshot(s)'],
    [status.ready_gifts_count, 'gift(s)'],
    [status.ready_season_passes_count, 'season pass(es)'],
  ];
  return counts
    .filter(([count]) => count > 0)
    .map(([count, label]) => `${count.toString()} ${label}`);
};

/** The selected Sandbox apart from the rest, as the switchers list them. */
export const splitSandboxes = (sandboxes: Sandbox[], code: string) => ({
  current: sandboxes.find((sandbox) => sandbox.code === code),
  others: sandboxes.filter((sandbox) => sandbox.code !== code),
});

// Narrowed rather than a bare boolean: every use of the counts is gated on it.
export const pendingPublish = (sandbox?: Pick<Sandbox, 'publish_status'>) =>
  sandbox?.publish_status?.has_pending ? sandbox.publish_status : null;

export const sandboxStateBadge = (state: SandboxState) =>
  state === 'published'
    ? ({ variant: 'success', label: 'Published' } as const)
    : ({ variant: 'warning', label: 'Dirty' } as const);
